import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import Icon1 from "@assets/icons/Icon";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { PDFViewerModal } from "@components/modals/PDFViewerModal";
import BillingCommentsModal from "@components/modals/BillingCommentsModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { PennylaneCredentialsCard } from "./PennylaneCredentialsCard";
import { Project, ProjectBillingStep, BillingComment, ProjectTheme } from "types";
import { projectsApi } from "@services/api";
import { formatEuros } from "@helpers/billingHelpers";
import { showNotification } from "context/notification.service";
import { useDataStore } from "@store/user_org";

const BASE_STATUS: Record<string, { label: string; dot: string }> = {
  pending: { label: "En attente", dot: "bg-neutral-300" },
  creating: { label: "Création et envoi en cours...", dot: "bg-amber-400 animate-pulse" },
  draft: { label: "Brouillon", dot: "bg-amber-400" },
  finalized: { label: "Finalisée", dot: "bg-amber-500" },
  sent: { label: "Envoyée", dot: "bg-green-500" },
  paid: { label: "Payée", dot: "bg-emerald-500" },
  error: { label: "Erreur", dot: "bg-red-500" },
};

function formatError(e: any): string {
  const d = e?.response?.data?.detail;
  if (!d) return "Une erreur est survenue";
  if (typeof d === "string") return d;
  return "Une erreur est survenue";
}

export function BillingTab({
  project,
  setProject,
  isGuest = false,
  isAdmin = false,
  canResend = false,
}: {
  project: Project;
  setProject: (p: Project) => void;
  isGuest?: boolean;
  isAdmin?: boolean;
  canResend?: boolean;
}) {
  const billing = project.billing;
  const userData = useDataStore((s) => s.userData);
  const pennylaneCompanyId = useDataStore((s) => s.pennylaneStatus?.company_id);
  const [loading, setLoading] = useState(false);
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());
  const [pdfModal, setPdfModal] = useState<{ url: string; title: string } | null>(null);
  // Admin-only: surface the raw error a billing step raised (creation /
  // finalize / send). The message is stored on the step as `invoice_error`.
  const [errorDetail, setErrorDetail] = useState<{ label: string; message: string } | null>(null);
  // Billing-thread chat — open state + optimistic unread override.
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [unreadOverride, setUnreadOverride] = useState<number | null>(null);
  const billingComments = billing?.comments || [];
  const storedUnread = userData?.id
    ? project.unseen_by_user?.[userData.id]?.billing_comments ?? 0
    : 0;
  const unreadComments = unreadOverride ?? storedUnread;
  const hasPdf = (step: ProjectBillingStep) => !!(step.invoice_id || (step.invoice_pdf_url && step.invoice_pdf_url.startsWith("http")));
  const [pdfLoadingIdx, setPdfLoadingIdx] = useState<number | null>(null);
  // Always fetch the PDF through the backend proxy, which re-resolves a fresh
  // URL from Pennylane. The stored `invoice_pdf_url` can be stale/manual and
  // stop opening (e.g. "app.pennylane.com refused to connect").
  const openPdfFor = async (stepIdx: number, title: string) => {
    setPdfLoadingIdx(stepIdx);
    try {
      const url = await projectsApi.getBillingPdfUrl(project.id, stepIdx);
      setPdfModal({ url, title });
    } catch (e) {
      showNotification({ type: "error", message: formatError(e) });
    } finally {
      setPdfLoadingIdx(null);
    }
  };
  const closePdf = () => {
    setPdfModal((cur) => {
      if (cur?.url?.startsWith("blob:")) URL.revokeObjectURL(cur.url);
      return null;
    });
  };

  // Days until project end (from cached planning_end_date on billing)
  const daysUntilEnd = billing?.planning_end_date
    ? Math.round((new Date(billing.planning_end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  // Track steps that need polling: only `creating` (a backend background
  // task is in flight and we want the row to flip to sent/error live).
  // Finalized+invoice_id used to trigger polling so the backend would
  // auto-retry the send; we no longer do that — manual liaisons must not
  // accidentally trigger an email — so finalized is a terminal state until
  // the user clicks "Envoyer au client".
  const hasCreating = billing?.steps?.some((s) => s.status === "creating") ?? false;
  const hasStuckFinalized = false;
  const needsPolling = hasCreating;

  // Retry state
  const retryCountRef = useRef<Record<number, number>>({});
  const MAX_RETRIES = 4;
  const RETRY_INTERVAL = 15; // seconds between sync retries

  // Countdown for UI
  const [retryCountdown, setRetryCountdown] = useState<number>(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }

    if (!needsPolling) {
      retryCountRef.current = {};
      setRetryCountdown(0);
      return;
    }

    const poll = async () => {
      try {
        if (hasStuckFinalized) {
          // Sync triggers auto-retry of sending finalized invoices on the backend
          const updated = await projectsApi.syncBilling(project.id);
          setProject(updated);
          // Track retry counts for finalized steps
          billing?.steps?.forEach((s, i) => {
            if (s.status === "finalized" && s.invoice_id) {
              retryCountRef.current[i] = (retryCountRef.current[i] ?? 0) + 1;
            }
          });
          // Clean up steps that resolved
          updated.billing?.steps?.forEach((s, i) => {
            if (s.status === "sent" || s.status === "paid") delete retryCountRef.current[i];
          });
        } else {
          // Just poll for creating → finalized/sent transition
          const updated = await projectsApi.get(project.id);
          setProject(updated);
          // Clear the "Envoi en cours..." visual once no step is creating.
          // setSendingKey(null) is idempotent — safe to call even if
          // sendingKey is already null (no unnecessary re-render).
          const stillCreating = updated.billing?.steps?.some(
            (s) => s.status === "creating",
          );
          if (!stillCreating) {
            setSendingKey(null);
          }
        }
      } catch {}
    };

    const interval = hasCreating ? 3000 : RETRY_INTERVAL * 1000;
    pollRef.current = setInterval(poll, interval);

    // Countdown timer for finalized retry UI
    if (hasStuckFinalized && !hasCreating) {
      setRetryCountdown(RETRY_INTERVAL);
      countdownRef.current = setInterval(() => {
        setRetryCountdown((prev) => (prev <= 1 ? RETRY_INTERVAL : prev - 1));
      }, 1000);
    }

    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    };
  }, [hasCreating, hasStuckFinalized]);

  // Auto-sync with Pennylane on mount
  const syncedRef = useRef(false);
  useEffect(() => {
    if (billing && !syncedRef.current) {
      syncedRef.current = true;
      const hasInvoices = billing.steps.some((s) => s.invoice_id);
      if (hasInvoices) {
        setSyncing(true);
        projectsApi.syncBilling(project.id)
          .then((updated) => setProject(updated))
          .catch(() => {})
          .finally(() => setSyncing(false));
      }
    }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const updated = await projectsApi.syncBilling(project.id);
      setProject(updated);
    } catch {
      showNotification({ type: "error", message: "Erreur de synchronisation" });
    } finally {
      setSyncing(false);
    }
  };

  const [repairing, setRepairing] = useState<"project" | number | null>(null);

  // A step "needs repair" when its local state is broken or inconsistent
  // with itself in a way the heal flow can fix:
  //   - explicit error
  //   - past pending but invoice_id wiped (the bug that prompted this whole
  //     feature: status=finalized, invoice_number set, invoice_id null)
  //   - finalized but PDF never arrived
  // When everything is coherent, the button is hidden so the UI stays clean.
  const stepNeedsRepair = (step: ProjectBillingStep): boolean => {
    if (step.status === "error") return true;
    const hasInvoiceTraces = !!(step.invoice_number || step.invoice_pdf_url);
    if (hasInvoiceTraces && !step.invoice_id) return true;
    if (step.status === "finalized" && !step.invoice_id) return true;
    if (step.status === "finalized" && !step.invoice_pdf_url) return true;
    return false;
  };
  const projectNeedsRepair = !!billing?.steps?.some(stepNeedsRepair);
  const handleRepairProject = async () => {
    setRepairing("project");
    try {
      const updated = await projectsApi.repairProjectBilling(project.id);
      setProject(updated);
      showNotification({ type: "success", message: "Facturation réparée" });
    } catch (e) {
      showNotification({ type: "error", message: formatError(e) });
    } finally {
      setRepairing(null);
    }
  };
  const handleRepairStep = async (stepIdx: number) => {
    setRepairing(stepIdx);
    try {
      const updated = await projectsApi.repairBillingStep(project.id, stepIdx);
      setProject(updated);
      showNotification({ type: "success", message: "Étape réparée" });
    } catch (e) {
      showNotification({ type: "error", message: formatError(e) });
    } finally {
      setRepairing(null);
    }
  };

  // Reset = wipe a step's runtime data and (if still draft on Pennylane)
  // delete the Pennylane invoice. Used when a step is wedged in 'creating'
  // or when the user wants to start over from scratch.
  const [resetTargetIdx, setResetTargetIdx] = useState<number | null>(null);
  const [resetting, setResetting] = useState(false);
  const handleResetStep = async (stepIdx: number, force = false) => {
    setResetting(true);
    try {
      const updated = await projectsApi.resetBillingStep(project.id, stepIdx, force);
      setProject(updated);
      showNotification({ type: "success", message: "Étape réinitialisée" });
      setResetTargetIdx(null);
    } catch (e) {
      showNotification({ type: "error", message: formatError(e) });
    } finally {
      setResetting(false);
    }
  };

  // Link = manually adopt an existing Pennylane invoice into a step.
  // Use case: invoice created directly in Pennylane's UI, needs to be
  // bound to a project step so payment status syncs.
  const [linkTargetIdx, setLinkTargetIdx] = useState<number | null>(null);
  const [linking, setLinking] = useState(false);
  // linkingKey mirrors sendingKey but for the link flow: it's the row
  // identifier currently being linked, so the row can show "Liaison en
  // cours..." instead of its static status while the API is in flight.
  const [linkingKey, setLinkingKey] = useState<string | null>(null);
  const handleLinkInvoice = async (stepIdx: number, invoiceRef: string, force: boolean) => {
    setLinking(true);
    // Demarrage steps render as a single grouped row keyed "demarrage".
    // Use that key when the target is a demarrage step so the indicator
    // attaches to the visible row, not an invisible per-step one.
    const targetStep = project.billing?.steps?.[stepIdx];
    setLinkingKey(targetStep?.is_demarrage ? "demarrage" : `step-${stepIdx}`);
    try {
      const updated = await projectsApi.linkPennylaneInvoice(project.id, stepIdx, invoiceRef, force);
      setProject(updated);
      showNotification({ type: "success", message: "Facture Pennylane liée" });
      setLinkTargetIdx(null);
    } catch (e) {
      showNotification({ type: "error", message: formatError(e) });
    } finally {
      setLinking(false);
      setLinkingKey(null);
    }
  };

  // Link controlpoints = (re)bind which controlpoints trigger a billing step.
  // Self-service fix when a step's link is broken/missing (e.g. inherited a
  // controlpoint id from a template that doesn't match this project), so
  // validating the right point never triggered the invoice.
  const [linkCpTargetIdx, setLinkCpTargetIdx] = useState<number | null>(null);
  const [linkingCp, setLinkingCp] = useState(false);
  const handleLinkControlpoints = async (stepIdx: number, controlpointIds: string[]) => {
    setLinkingCp(true);
    try {
      const updated = await projectsApi.linkControlpoints(project.id, stepIdx, controlpointIds);
      setProject(updated);
      showNotification({ type: "success", message: "Points de contrôle liés à l'étape" });
      setLinkCpTargetIdx(null);
    } catch (e) {
      showNotification({ type: "error", message: formatError(e) });
    } finally {
      setLinkingCp(false);
    }
  };

  if (!billing) {
    return (
      <div className="text-h3 text-neutral-400 py-12 text-center">
        Aucune facturation configurée pour ce projet.
      </div>
    );
  }

  const { steps, montant_total } = billing;
  const indexed = steps.map((s, i) => ({ ...s, idx: i }));
  const s = (status: string, _stepIdx?: number) => {
    const base = BASE_STATUS[status] || BASE_STATUS.pending;
    if (status === "creating") {
      return { label: "Création et envoi en cours...", dot: "bg-amber-400 animate-pulse" };
    }
    return base;
  };

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex flex-row items-start justify-between py-1.5">
      <div className="text-h4 text-neutral-400">{label}</div>
      <div className="text-h4 text-neutral-700 text-right">{value}</div>
    </div>
  );

  // Guest view: only show sent/paid invoices with PDF access
  if (isGuest) {
    const visibleSteps = indexed.filter((s) => ["sent", "paid"].includes(s.status));
    if (visibleSteps.length === 0) {
      return (
        <div className="text-h3 text-neutral-400 py-12 text-center">
          Aucune facture disponible pour le moment.
        </div>
      );
    }

    // Group demarrage steps into one entry
    const guestDemarrage = visibleSteps.filter((s) => s.is_demarrage);
    const guestAvancement = visibleSteps.filter((s) => !s.is_demarrage);
    const TVA = 1.2;
    const guestDemarrageTotal = guestDemarrage.reduce((sum, s) => sum + s.montant, 0);

    const GuestStepCard = ({ label, montant, status, invoiceNumber, sentDates, sentAt, paidAt, pdfUrl, stepIdx }: {
      label: string; montant: number; status: string; invoiceNumber?: string;
      sentDates?: string[]; sentAt?: string; paidAt?: string; pdfUrl?: string; stepIdx: number;
    }) => (
      <div className="rounded-lg border bg-white border-neutral-200 p-4 flex flex-col gap-2">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${s(status).dot}`} />
            <div className="text-h3 font-medium text-neutral-800">{label}</div>
          </div>
          <div className="text-h3 font-medium text-neutral-700">{formatEuros(montant * TVA)} € TTC</div>
        </div>
        <div className="flex flex-col divide-y divide-neutral-50">
          <DetailRow label="Statut" value={<span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${s(status).dot}`} />{status === "paid" ? "Payée" : "Envoyée"}</span>} />
          {invoiceNumber && <DetailRow label="N° facture" value={invoiceNumber} />}
          {sentDates && sentDates.length > 0 ? (
            sentDates.map((d, i) => (
              <DetailRow key={i} label={i === 0 ? "Envoyée le" : "Renvoyée le"} value={new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
            ))
          ) : sentAt ? (
            <DetailRow label="Envoyée le" value={new Date(sentAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
          ) : null}
          {paidAt && <DetailRow label="Payée le" value={new Date(paidAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
        </div>
        {pdfUrl && pdfUrl.startsWith("http") && (
          <button
            className="mt-1 flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 w-fit disabled:opacity-50"
            disabled={pdfLoadingIdx === stepIdx}
            onClick={() => openPdfFor(stepIdx, label)}
          >
            <Icon1 name="pdf" size={16} />
            {pdfLoadingIdx === stepIdx ? "Chargement..." : "Voir la facture"}
          </button>
        )}
      </div>
    );

    return (
      <div className="flex flex-col gap-2 w-full max-w-500">
        {guestDemarrage.length > 0 && (
          <GuestStepCard
            label="Démarrage"
            montant={guestDemarrageTotal}
            status={guestDemarrage[0].status}
            invoiceNumber={guestDemarrage[0].invoice_number}
            sentDates={guestDemarrage[0].sent_dates}
            sentAt={guestDemarrage[0].sent_at}
            paidAt={guestDemarrage[0].paid_at}
            pdfUrl={guestDemarrage[0].invoice_pdf_url}
            stepIdx={guestDemarrage[0].idx}
          />
        )}
        {guestAvancement.map((step) => (
          <GuestStepCard
            key={step.idx}
            label={step.label}
            montant={step.montant}
            status={step.status}
            invoiceNumber={step.invoice_number}
            sentDates={step.sent_dates}
            sentAt={step.sent_at}
            paidAt={step.paid_at}
            pdfUrl={step.invoice_pdf_url}
            stepIdx={step.idx}
          />
        ))}
        <div className="flex items-center justify-end gap-2 px-4 py-2">
          <div className="text-h3 text-neutral-400">Total TTC</div>
          <div className="text-h2 font-semibold text-neutral-800">{formatEuros(montant_total * 1.2)} €</div>
        </div>
        {pdfModal && (
          <PDFViewerModal pdfUrl={pdfModal.url} title={`Facture — ${pdfModal.title}`} onClose={closePdf} />
        )}
      </div>
    );
  }

  const demarrageSteps = indexed.filter((s) => s.is_demarrage);
  const avancementSteps = indexed.filter((s) => !s.is_demarrage);
  const demarrageTotal = demarrageSteps.reduce((sum, s) => sum + s.montant, 0);
  const demarrageStatus = demarrageSteps[0]?.status || "pending";

  const act = async (fn: () => Promise<Project>, msg: string, key?: string) => {
    setLoading(true);
    if (key) setSendingKey(key);
    try {
      const updated = await fn();
      setProject(updated);
      if (msg) showNotification({ type: "success", message: msg });
      // Keep the "Envoi en cours..." visual alive while a background task
      // is still processing (status = "creating"). The polling interval
      // will clear it once the step reaches a final status.
      const stillCreating = updated.billing?.steps?.some(
        (s) => s.status === "creating",
      );
      if (!stillCreating) {
        setSendingKey(null);
      }
    } catch (e) {
      showNotification({ type: "error", message: formatError(e) });
      setSendingKey(null);
    } finally {
      setLoading(false);
    }
  };

  // Resolve controlpoint IDs to full path: theme > step > controlpoint
  const cpInfoMap = new Map<string, { cp: string; step: string; theme: string }>();
  for (const theme of project.themes || []) {
    for (const step of theme.steps) {
      for (const cp of step.controlpoints) {
        cpInfoMap.set(cp.id, { cp: cp.label, step: step.label, theme: theme.label });
      }
    }
  }

  const linkedInfos = (ids: string[]) => {
    if (!ids || ids.length === 0) return [];
    return ids.map((id) => cpInfoMap.get(id)).filter(Boolean) as { cp: string; step: string; theme: string }[];
  };

  const toggle = (key: string) => setExpandedSet((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const isExpanded = (key: string) => expandedSet.has(key);

  const StepExpanded = ({ step }: { step: ProjectBillingStep & { idx: number } }) => {
    const linked = linkedInfos(step.linked_step_ids);
    return (
      <div className="px-4 pb-3 pt-1 border-t border-neutral-100 flex flex-col">
        <div className="flex flex-col divide-y divide-neutral-50">
          <DetailRow label="Montant TTC" value={`${formatEuros(step.montant * 1.2)} €`} />
          <DetailRow label="Pourcentage" value={`${step.percentage}%`} />
          <DetailRow label="Statut" value={
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${s(step.status, step.idx).dot}`} />
              {s(step.status, step.idx).label}
            </span>
          } />
          {step.status === "sent" && step.sent_at && (() => {
            const delay = step.payment_delay_days ?? 15;
            const daysLate = Math.floor((Date.now() - new Date(step.sent_at!).getTime()) / (1000 * 60 * 60 * 24)) - delay;
            if (daysLate <= 0) return null;
            return (
              <div className="py-2 px-3 my-1 rounded-lg bg-red-50 border border-red-100">
                <div className="text-h4 text-red-700 font-medium">Retard de paiement</div>
                <div className="text-h4 text-red-600">
                  Cette facture a été envoyée il y a {daysLate + delay} jours (délai : {delay} jours). Retard de {daysLate} jour{daysLate > 1 ? "s" : ""}.
                </div>
              </div>
            );
          })()}
          {step.status === "pending" && step.project_delay_days && daysUntilEnd != null && daysUntilEnd <= step.project_delay_days && (
            <div className="py-2 px-3 my-1 rounded-lg bg-red-50 border border-red-100">
              <div className="text-h4 text-red-700 font-medium">Retard projet</div>
              <div className="text-h4 text-red-600">
                La fin du projet est prévue dans {daysUntilEnd} jour{daysUntilEnd > 1 ? "s" : ""} et cette facture n'a pas encore été créée.
              </div>
            </div>
          )}
          {step.invoice_number && <DetailRow label="N° facture" value={step.invoice_number} />}
          {linked.length > 0 && (
            <div className="py-1.5">
              <div className="text-h4 text-neutral-400 mb-1">Étape liée</div>
              {linked.map((info, i) => (
                <div key={i} className="text-h4 text-neutral-700">
                  {info.theme} › {info.step} › {info.cp}
                </div>
              ))}
            </div>
          )}
          {step.drafted_at && <DetailRow label="Brouillon créé le" value={new Date(step.drafted_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
          {step.finalized_at && <DetailRow label="Finalisée le" value={new Date(step.finalized_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
          {step.sent_dates && step.sent_dates.length > 0 ? (
            step.sent_dates.map((d, i) => (
              <DetailRow key={`sent-${i}`} label={i === 0 ? "Envoyée le" : "Renvoyée le"} value={new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
            ))
          ) : step.sent_at ? (
            <DetailRow label="Envoyée le" value={new Date(step.sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
          ) : null}
          {step.last_relance_sent_at && (
            <DetailRow
              label="Relance demandée le"
              value={new Date(step.last_relance_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
            />
          )}
          {step.last_conductor_alert_sent_at && (
            <DetailRow
              label="Alerte interne envoyée le"
              value={new Date(step.last_conductor_alert_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
            />
          )}
          {step.paid_at && <DetailRow label="Payée le" value={new Date(step.paid_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
        </div>

        {/* PDF button */}
        {hasPdf(step) && (
          <button
            className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-50"
            disabled={pdfLoadingIdx === step.idx}
            onClick={() => openPdfFor(step.idx, step.label)}
          >
            <Icon1 name="pdf" size={16} />
            {pdfLoadingIdx === step.idx ? "Chargement..." : "Voir la facture"}
          </button>
        )}

        {/* Links + Actions */}
        <div className="flex flex-row items-center gap-2 mt-3">
          {isAdmin && step.invoice_id && pennylaneCompanyId && (
            <a href={`https://app.pennylane.com/companies/${pennylaneCompanyId}/clients/customer_invoices?invoice_id=${step.invoice_id}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50">
              <Icon1 name="open" size={14} />
              Pennylane
            </a>
          )}
          {isAdmin && stepNeedsRepair(step) && (
            <button
              disabled={repairing !== null || loading}
              onClick={() => handleRepairStep(step.idx)}
              title="Réconcilier l'étape avec Pennylane (sans créer ni renvoyer)"
              className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-50"
            >
              <Icon1 name="tool" size={14} />
              {repairing === step.idx ? "Réparation..." : "Réparer"}
            </button>
          )}
          {isAdmin && step.invoice_error && (
            <button
              onClick={() => setErrorDetail({ label: step.label, message: step.invoice_error! })}
              title="Voir le détail de l'erreur"
              className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
            >
              <Icon1 name="warning" size={14} />
              Voir l'erreur
            </button>
          )}
          <div className="flex-1" />
          {step.status === "pending" && (
            <SecondaryButton variant="small" disabled={loading} label={sendingKey === `step-${step.idx}` ? "Envoi en cours..." : "Envoyer"}
              onClick={() => act(() => projectsApi.triggerBillingInvoice(project.id, step.idx), "", `step-${step.idx}`)} />
          )}
          {step.status === "draft" && (
            <SecondaryButton variant="small" disabled={loading} label={sendingKey === `step-${step.idx}` ? "Envoi en cours..." : "Finaliser et envoyer"}
              onClick={() => act(async () => {
                // Reflect the finalize immediately, then let a send failure
                // surface as an error toast — do NOT swallow it, otherwise the
                // step shows "Envoyée" while the client was never emailed.
                const finalized = await projectsApi.finalizeBillingInvoice(project.id, step.idx);
                setProject(finalized);
                return await projectsApi.sendBillingInvoice(project.id, step.idx);
              }, "Facture finalisée et envoyée", `step-${step.idx}`)} />
          )}
          {step.status === "finalized" && (
            <SecondaryButton variant="small" disabled={loading} label={sendingKey === `step-${step.idx}` ? "Envoi en cours..." : "Envoyer au client"}
              onClick={() => act(() => projectsApi.sendBillingInvoice(project.id, step.idx), "Facture envoyée", `step-${step.idx}`)} />
          )}
          {step.status === "error" && (
            <SecondaryButton variant="small" disabled={loading} label={sendingKey === `step-${step.idx}` ? "Envoi en cours..." : "Réessayer"}
              onClick={() => act(() => projectsApi.retryBillingInvoice(project.id, step.idx), "", `step-${step.idx}`)} />
          )}
          <StepActionsMenu
            step={step}
            onLink={() => setLinkTargetIdx(step.idx)}
            onLinkCp={() => setLinkCpTargetIdx(step.idx)}
            onReset={() => setResetTargetIdx(step.idx)}
          />
          {step.status === "sent" && canResend && (
            <button
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-50"
              onClick={() => act(() => projectsApi.resendBillingInvoice(project.id, step.idx), "Demande de relance envoyée au comptable", `step-${step.idx}`)}
            >
              <Icon1 name="send" size={14} />
              {sendingKey === `step-${step.idx}` ? "Envoi en cours..." : "Demander une relance"}
            </button>
          )}
        </div>
      </div>
    );
  };

  const resetTargetStep =
    resetTargetIdx !== null ? billing?.steps?.[resetTargetIdx] : undefined;
  const resetTargetLabel = resetTargetStep?.label || `étape ${(resetTargetIdx ?? 0) + 1}`;
  const resetTargetStatus = resetTargetStep?.status;
  // Steps that have already involved a real client interaction (sent or
  // paid) need ``force`` because Pennylane refuses to delete the invoice
  // and we'd be detaching from a real accounting record.
  const resetNeedsForce =
    resetTargetStatus === "sent" || resetTargetStatus === "paid" || resetTargetStatus === "finalized";

  const linkTargetStep =
    linkTargetIdx !== null ? billing?.steps?.[linkTargetIdx] : undefined;
  const linkTargetLabel = linkTargetStep?.label || `étape ${(linkTargetIdx ?? 0) + 1}`;

  return (
    <div className="flex flex-col gap-1 w-full max-w-500">
      {resetTargetIdx !== null && (
        <ConfirmationModal
          title={`Réinitialiser « ${resetTargetLabel} » ?`}
          message={
            resetTargetStatus === "draft" || resetTargetStatus === "creating"
              ? "Le brouillon sera supprimé chez Pennylane et l'étape repassera en attente. Vous pourrez ensuite relancer la facturation."
              : resetNeedsForce
                ? "ATTENTION : cette étape est finalisée/envoyée/payée chez Pennylane. L'étape sera détachée localement mais la facture restera dans Pennylane. Supprimez-la ou émettez un avoir manuellement si besoin."
                : "L'étape sera détachée de sa facture Pennylane et repassera en attente. La facture existante restera dans Pennylane — supprimez-la ou émettez un avoir manuellement si besoin."
          }
          confirmText={resetting ? "RÉINITIALISATION..." : "CONFIRMER"}
          onClose={() => !resetting && setResetTargetIdx(null)}
          onConfirm={() =>
            resetTargetIdx !== null && handleResetStep(resetTargetIdx, resetNeedsForce)
          }
        />
      )}
      {linkTargetIdx !== null && (
        <LinkPennylaneInvoiceModal
          stepLabel={linkTargetLabel}
          existingInvoiceId={linkTargetStep?.invoice_id || null}
          submitting={linking}
          onClose={() => !linking && setLinkTargetIdx(null)}
          onSubmit={(ref, force) =>
            linkTargetIdx !== null && handleLinkInvoice(linkTargetIdx, ref, force)
          }
        />
      )}
      {linkCpTargetIdx !== null && (
        <LinkControlpointsModal
          stepLabel={billing?.steps?.[linkCpTargetIdx]?.label || `étape ${linkCpTargetIdx + 1}`}
          themes={project.themes || []}
          currentLinkedIds={billing?.steps?.[linkCpTargetIdx]?.linked_step_ids || []}
          submitting={linkingCp}
          onClose={() => !linkingCp && setLinkCpTargetIdx(null)}
          onSubmit={(ids) => linkCpTargetIdx !== null && handleLinkControlpoints(linkCpTargetIdx, ids)}
        />
      )}
      {/* Pennylane connection — admin-only, lives directly in the
          Facturation tab so admins can configure the integration without
          a separate settings page. Becomes invisible once connected
          (collapsed to a single ID line + edit/disconnect actions). */}
      <PennylaneCredentialsCard isAdmin={isAdmin} />

      {/* Billing-thread chat — visible only to org members (the parent
          already gates this whole tab away from guests). */}
      <button
        className={`w-full flex flex-row items-center justify-between px-4 py-2.5 mb-1 rounded-lg border transition-colors ${
          unreadComments > 0
            ? "bg-red-50 border-red-200 hover:bg-red-100"
            : "bg-white border-neutral-200 hover:bg-neutral-50"
        }`}
        onClick={() => {
          setCommentsOpen(true);
          setUnreadOverride(0);
        }}
      >
        <div className="flex flex-row items-center gap-2">
          <div className="relative">
            <Icon1 name="message" size={18} color={unreadComments > 0 ? "#ef4444" : "#737373"} />
            {unreadComments > 0 && (
              <span className="absolute -top-1 -right-1.5 inline-flex items-center justify-center min-w-4 h-4 px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full">
                {unreadComments > 9 ? "!" : unreadComments}
              </span>
            )}
          </div>
          <div className="flex flex-col items-start">
            <div className={`text-h3 font-medium ${unreadComments > 0 ? "text-red-700" : "text-neutral-800"}`}>
              {unreadComments > 0
                ? `${unreadComments} nouveau${unreadComments > 1 ? "x" : ""} commentaire${unreadComments > 1 ? "s" : ""}`
                : "Commentaires facturation"}
            </div>
            <div className="text-h4 text-neutral-400">
              {billingComments.length === 0
                ? "Démarrer la conversation avec le conducteur et les comptables"
                : `${billingComments.length} message${billingComments.length > 1 ? "s" : ""} dans le fil`}
            </div>
          </div>
        </div>
        <div className="text-h4 text-neutral-400">Ouvrir →</div>
      </button>

      {/* Démarrage group */}
      {demarrageSteps.length > 0 && (
        <div className="rounded-lg border bg-white border-neutral-200 overflow-hidden">
          <button
            className="w-full flex flex-row items-center justify-between px-4 py-2.5 hover:bg-neutral-50 transition-colors"
            onClick={() => toggle("demarrage")}
          >
            <div className="flex flex-row items-center gap-2 flex-1">
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sendingKey === "demarrage" || linkingKey === "demarrage" ? "bg-amber-400 animate-pulse" : s(demarrageStatus, demarrageSteps[0]?.idx).dot}`} />
              <div className="flex flex-col items-start">
                <div className="text-h3 font-medium text-neutral-800">Démarrage</div>
                <div className="text-h4 text-neutral-400">
                  {linkingKey === "demarrage"
                    ? "Liaison en cours..."
                    : sendingKey === "demarrage"
                      ? "Envoi en cours..."
                      : s(demarrageStatus, demarrageSteps[0]?.idx).label}
                </div>
              </div>
            </div>
            <div className="flex flex-row items-center gap-3">
              <div className="text-h3 font-medium text-neutral-700">
                {formatEuros(demarrageTotal * 1.2)} €
              </div>
              <svg className={`w-4 h-4 text-neutral-400 transition-transform ${isExpanded("demarrage") ? "rotate-180" : ""}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {isExpanded("demarrage") && (
            <div className="px-4 pb-3 pt-1 border-t border-neutral-100 flex flex-col">
              <div className="flex flex-col divide-y divide-neutral-50">
                <DetailRow label="Montant TTC" value={`${formatEuros(demarrageTotal * 1.2)} €`} />
                <DetailRow label="Statut" value={
                  <span className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${s(demarrageStatus, demarrageSteps[0]?.idx).dot}`} />
                    {s(demarrageStatus, demarrageSteps[0]?.idx).label}
                  </span>
                } />
                {demarrageSteps[0]?.invoice_number && <DetailRow label="N° facture" value={demarrageSteps[0].invoice_number} />}
                {demarrageSteps.map((st, i) => (
                  <DetailRow key={i} label={st.label} value={`${formatEuros(st.montant * 1.2)} € (${st.percentage}%)`} />
                ))}
                {demarrageSteps[0]?.drafted_at && <DetailRow label="Brouillon créé le" value={new Date(demarrageSteps[0].drafted_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
                {demarrageSteps[0]?.finalized_at && <DetailRow label="Finalisée le" value={new Date(demarrageSteps[0].finalized_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
                {demarrageSteps[0]?.sent_dates && demarrageSteps[0].sent_dates.length > 0 ? (
                  demarrageSteps[0].sent_dates.map((d, i) => (
                    <DetailRow key={`sent-${i}`} label={i === 0 ? "Envoyée le" : "Renvoyée le"} value={new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
                  ))
                ) : demarrageSteps[0]?.sent_at ? (
                  <DetailRow label="Envoyée le" value={new Date(demarrageSteps[0].sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
                ) : null}
                {demarrageSteps[0]?.last_relance_sent_at && (
                  <DetailRow
                    label="Relance demandée le"
                    value={new Date(demarrageSteps[0].last_relance_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                  />
                )}
                {demarrageSteps[0]?.last_conductor_alert_sent_at && (
                  <DetailRow
                    label="Alerte interne envoyée le"
                    value={new Date(demarrageSteps[0].last_conductor_alert_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                  />
                )}
                {demarrageSteps[0]?.paid_at && <DetailRow label="Payée le" value={new Date(demarrageSteps[0].paid_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
              </div>
              {demarrageSteps[0] && hasPdf(demarrageSteps[0]) && (
                <button
                  className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-50"
                  disabled={pdfLoadingIdx === demarrageSteps[0].idx}
                  onClick={() => openPdfFor(demarrageSteps[0].idx, "Démarrage")}
                >
                  <Icon1 name="pdf" size={16} />
                  {pdfLoadingIdx === demarrageSteps[0].idx ? "Chargement..." : "Voir la facture"}
                </button>
              )}
              <div className="flex flex-row items-center gap-2 mt-3">
                {isAdmin && demarrageSteps[0]?.invoice_id && pennylaneCompanyId && (
                  <a href={`https://app.pennylane.com/companies/${pennylaneCompanyId}/clients/customer_invoices?invoice_id=${demarrageSteps[0].invoice_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50">
                    <Icon1 name="open" size={14} />
                    Pennylane
                  </a>
                )}
                {isAdmin && demarrageSteps[0]?.invoice_error && (
                  <button
                    onClick={() => setErrorDetail({ label: "Démarrage", message: demarrageSteps[0].invoice_error! })}
                    title="Voir le détail de l'erreur"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <Icon1 name="warning" size={14} />
                    Voir l'erreur
                  </button>
                )}
                <div className="flex-1" />
                {demarrageStatus === "pending" && (
                  <SecondaryButton variant="small" disabled={loading} label={sendingKey === "demarrage" ? "Envoi en cours..." : "Envoyer"}
                    onClick={() => act(() => projectsApi.triggerDemarrageInvoice(project.id), "", "demarrage")} />
                )}
                {demarrageStatus === "error" && (
                  <SecondaryButton variant="small" disabled={loading} label={sendingKey === "demarrage" ? "Envoi en cours..." : "Réessayer"}
                    onClick={() => act(() => projectsApi.triggerDemarrageInvoice(project.id), "", "demarrage")} />
                )}
                {demarrageStatus === "draft" && (
                  <SecondaryButton variant="small" disabled={loading} label={sendingKey === "demarrage" ? "Envoi en cours..." : "Finaliser et envoyer"}
                    onClick={() => act(async () => {
                      // Reflect the finalize immediately, then let a send
                      // failure surface as an error toast — do NOT swallow it.
                      const finalized = await projectsApi.finalizeBillingInvoice(project.id, demarrageSteps[0].idx);
                      setProject(finalized);
                      return await projectsApi.sendBillingInvoice(project.id, demarrageSteps[0].idx);
                    }, "Facture finalisée et envoyée", "demarrage")} />
                )}
                {demarrageStatus === "finalized" && (
                  <SecondaryButton variant="small" disabled={loading} label={sendingKey === "demarrage" ? "Envoi en cours..." : "Envoyer au client"}
                    onClick={() => act(() => projectsApi.sendBillingInvoice(project.id, demarrageSteps[0].idx), "Facture envoyée", "demarrage")} />
                )}
                {demarrageStatus === "sent" && canResend && (
                  <button
                    disabled={loading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-50"
                    onClick={() => act(() => projectsApi.resendBillingInvoice(project.id, demarrageSteps[0].idx), "Demande de relance envoyée au comptable", "demarrage")}
                  >
                    <Icon1 name="send" size={14} />
                    {sendingKey === "demarrage" ? "Envoi en cours..." : "Demander une relance"}
                  </button>
                )}
                {demarrageSteps[0] && (
                  <StepActionsMenu
                    step={demarrageSteps[0]}
                    onLink={() => setLinkTargetIdx(demarrageSteps[0].idx)}
                    onLinkCp={() => setLinkCpTargetIdx(demarrageSteps[0].idx)}
                    onReset={() => setResetTargetIdx(demarrageSteps[0].idx)}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Avancement steps */}
      {avancementSteps.map((step) => (
        <div key={step.idx} className="rounded-lg border bg-white border-neutral-200 overflow-hidden">
          <button
            className="w-full flex flex-row items-center justify-between px-4 py-2.5 hover:bg-neutral-50 transition-colors"
            onClick={() => toggle(`step-${step.idx}`)}
          >
            <div className="flex flex-row items-center gap-2 flex-1">
              {(() => {
                const isProjectDelay = step.status === "pending" && step.project_delay_days && daysUntilEnd != null && daysUntilEnd <= step.project_delay_days;
                const isLatePayment = step.status === "sent" && step.sent_at && (() => {
                  const delay = step.payment_delay_days ?? 15;
                  return (Date.now() - new Date(step.sent_at!).getTime()) / (1000 * 60 * 60 * 24) > delay;
                })();
                // Step gap: find previous step's sent_at
                const isStepGap = step.step_gap_delay_days && ["pending", "draft", "finalized"].includes(step.status) && (() => {
                  const myIndex = avancementSteps.findIndex((s) => s.idx === step.idx);
                  if (myIndex <= 0) return false;
                  const prev = avancementSteps[myIndex - 1];
                  if (!prev.sent_at || !["sent", "paid"].includes(prev.status)) return false;
                  const daysSincePrev = (Date.now() - new Date(prev.sent_at).getTime()) / (1000 * 60 * 60 * 24);
                  return daysSincePrev > step.step_gap_delay_days!;
                })();
                const isRetard = isProjectDelay || isLatePayment || isStepGap;
                const isSending = sendingKey === `step-${step.idx}`;
                const isLinking = linkingKey === `step-${step.idx}`;
                return <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isSending || isLinking ? "bg-amber-400 animate-pulse" : isRetard ? "bg-red-500" : s(step.status, step.idx).dot}`} />;
              })()}
              <div className="flex flex-col items-start">
                <div className="text-h3 font-medium text-neutral-800">{step.label}</div>
                <div className="text-h4 text-neutral-400">
                  {linkingKey === `step-${step.idx}`
                    ? `${step.percentage}% · Liaison en cours...`
                    : sendingKey === `step-${step.idx}`
                      ? `${step.percentage}% · Envoi en cours...`
                      : `${step.percentage}% · ${s(step.status, step.idx).label}`}
                </div>
                {step.status === "sent" && step.sent_at && (() => {
                  const delay = step.payment_delay_days ?? 15;
                  const daysLate = Math.floor((Date.now() - new Date(step.sent_at!).getTime()) / (1000 * 60 * 60 * 24)) - delay;
                  if (daysLate <= 0) return null;
                  return (
                    <div className="text-h5 text-red-600 font-medium">
                      Retard de paiement — {daysLate} jour{daysLate > 1 ? "s" : ""} de retard
                    </div>
                  );
                })()}
                {step.step_gap_delay_days && ["pending", "draft", "finalized"].includes(step.status) && (() => {
                  const myIndex = avancementSteps.findIndex((s) => s.idx === step.idx);
                  if (myIndex <= 0) return null;
                  const prev = avancementSteps[myIndex - 1];
                  if (!prev.sent_at || !["sent", "paid"].includes(prev.status)) return null;
                  const daysSincePrev = Math.floor((Date.now() - new Date(prev.sent_at).getTime()) / (1000 * 60 * 60 * 24));
                  const daysLate = daysSincePrev - step.step_gap_delay_days!;
                  if (daysLate <= 0) return null;
                  return (
                    <div className="text-h5 text-red-600 font-medium">
                      Retard entre étapes — {daysLate} jour{daysLate > 1 ? "s" : ""} après l'étape précédente
                    </div>
                  );
                })()}
                {step.status === "pending" && step.project_delay_days && daysUntilEnd != null && daysUntilEnd <= step.project_delay_days && (
                  <div className="text-h5 text-red-600 font-medium">
                    Retard projet — fin dans {daysUntilEnd} jour{daysUntilEnd > 1 ? "s" : ""}, facture non créée
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-row items-center gap-3">
              <div className="text-h3 font-medium text-neutral-700">
                {formatEuros(step.montant * 1.2)} €
              </div>
              <svg className={`w-4 h-4 text-neutral-400 transition-transform ${isExpanded(`step-${step.idx}`) ? "rotate-180" : ""}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {isExpanded(`step-${step.idx}`) && <StepExpanded step={step} />}
        </div>
      ))}

      {/* Footer: sync + total */}
      <div className="flex flex-row items-center justify-between px-4 py-2 mt-1">
        <div className="flex items-center gap-3">
          <button
            onClick={handleSync}
            disabled={syncing || repairing !== null}
            className="flex items-center gap-1.5 text-h4 text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
          >
            <Icon1 name="sync" size={14} />
            {syncing ? "Synchronisation..." : "Synchroniser"}
          </button>
          {isAdmin && projectNeedsRepair && (
            <button
              onClick={handleRepairProject}
              disabled={repairing !== null || syncing}
              title="Réconcilier toutes les étapes avec Pennylane (sans créer ni renvoyer)"
              className="flex items-center gap-1.5 text-h4 text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
            >
              <Icon1 name="tool" size={14} />
              {repairing === "project" ? "Réparation..." : "Tout réparer"}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-h3 text-neutral-400">Total TTC</div>
          <div className="text-h2 font-semibold text-neutral-800">
            {formatEuros(montant_total * 1.2)} €
          </div>
        </div>
      </div>

      {pdfModal && (
        <PDFViewerModal
          pdfUrl={pdfModal.url}
          title={`Facture — ${pdfModal.title}`}
          onClose={closePdf}
        />
      )}

      {errorDetail && (
        <BillingErrorModal
          stepLabel={errorDetail.label}
          message={errorDetail.message}
          onClose={() => setErrorDetail(null)}
        />
      )}

      {commentsOpen && (
        <BillingCommentsModal
          projectId={project.id}
          projectName={project.infos?.general?.name}
          initialComments={billingComments}
          onCommentsChange={(next: BillingComment[]) => {
            // Mirror the new list into the parent project so subsequent
            // opens start with up-to-date comments.
            if (project.billing) {
              setProject({ ...project, billing: { ...project.billing, comments: next } });
            }
          }}
          onClose={() => setCommentsOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-step action menu (vertical 3 dots). Lives next to the existing status-
// specific buttons. Surfaces manual actions that should always be reachable
// — link an existing Pennylane invoice, fully reset the step — even when the
// step has otherwise nothing actionable in its normal lifecycle.
// ---------------------------------------------------------------------------
function StepActionsMenu({
  step,
  onLink,
  onLinkCp,
  onReset,
}: {
  step: ProjectBillingStep;
  onLink: () => void;
  onLinkCp: () => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // The popover is rendered into document.body via a portal because the
  // step row uses `overflow-hidden` (to clip the rounded corners of its
  // expanded inner content). An absolute popover would be clipped — that
  // was the "click does nothing" bug.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const POPOVER_WIDTH = 260; // matches w-64 below
    setCoords({
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - POPOVER_WIDTH),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t)) return;
      if (popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="Actions"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center w-8 h-8 rounded-lg text-neutral-500 hover:bg-neutral-100"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <circle cx="8" cy="3" r="1.6" />
          <circle cx="8" cy="8" r="1.6" />
          <circle cx="8" cy="13" r="1.6" />
        </svg>
      </button>
      {open && coords &&
        createPortal(
          <div
            ref={popRef}
            style={{ position: "fixed", top: coords.top, left: coords.left, width: 260 }}
            className="z-[70] rounded-lg border border-neutral-200 bg-white shadow-lg py-1 text-h4"
          >
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center gap-2"
              onClick={() => {
                setOpen(false);
                onLink();
              }}
            >
              <Icon1 name="join" size={14} />
              <span>
                {step.invoice_id ? "Remplacer la facture liée" : "Lier une facture Pennylane"}
              </span>
            </button>
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center gap-2"
              onClick={() => {
                setOpen(false);
                onLinkCp();
              }}
            >
              <Icon1 name="link" size={14} />
              <span>
                {(step.linked_step_ids?.length ?? 0) > 0
                  ? "Modifier le point de contrôle déclencheur"
                  : "Lier un point de contrôle déclencheur"}
              </span>
            </button>
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center gap-2 text-red-500"
              onClick={() => {
                setOpen(false);
                onReset();
              }}
            >
              <Icon1 name="restart" size={14} />
              <span>Réinitialiser l'étape</span>
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Modal for manually linking an existing Pennylane invoice to a step.
// Accepts either a numeric Pennylane invoice ID (the one shown in the URL,
// e.g. `?invoice_id=12345`) or the human invoice number (e.g. F-2026-0042).
// ---------------------------------------------------------------------------
function LinkPennylaneInvoiceModal({
  stepLabel,
  existingInvoiceId,
  submitting,
  onClose,
  onSubmit,
}: {
  stepLabel: string;
  existingInvoiceId: number | string | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (ref: string, force: boolean) => void;
}) {
  const [ref, setRef] = useState("");
  const force = !!existingInvoiceId;
  const canSubmit = ref.trim().length > 0 && !submitting;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 flex flex-col gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-h2 font-medium text-black">
          Lier une facture Pennylane — « {stepLabel} »
        </div>
        <div className="text-h4 text-neutral-500">
          Collez l'ID Pennylane (numérique, visible dans l'URL de la facture) ou
          le numéro de facture (ex&nbsp;: F-2026-0042). La facture sera adoptée
          et son statut, son PDF et les dates seront synchronisés.
        </div>
        {existingInvoiceId && (
          <div className="text-h4 text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
            Cette étape est déjà liée à la facture <b>{existingInvoiceId}</b>.
            Confirmer la liaison remplacera ce lien.
          </div>
        )}
        <input
          type="text"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder="123456 ou F-2026-0042"
          className="border border-neutral-300 rounded-lg px-3 py-2 text-h3"
          autoFocus
          disabled={submitting}
        />
        <div className="flex flex-row justify-end gap-2 pt-1">
          <button
            type="button"
            className="px-4 py-2 rounded-lg text-h3 text-neutral-600 hover:bg-neutral-100"
            onClick={onClose}
            disabled={submitting}
          >
            Annuler
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-lg text-h3 bg-black text-white disabled:opacity-50"
            disabled={!canSubmit}
            onClick={() => onSubmit(ref.trim(), force)}
          >
            {submitting ? "Liaison..." : "Lier"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal to (re)bind which controlpoint(s) trigger a billing step. Writes the
// step's `linked_step_ids`. Self-service repair for a broken/missing link —
// validating the chosen controlpoint will then trigger this invoice, and
// sending the invoice will green that controlpoint.
// ---------------------------------------------------------------------------
function LinkControlpointsModal({
  stepLabel,
  themes,
  currentLinkedIds,
  submitting,
  onClose,
  onSubmit,
}: {
  stepLabel: string;
  themes: ProjectTheme[];
  currentLinkedIds: string[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: (ids: string[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(currentLinkedIds));
  const [query, setQuery] = useState("");

  const stateBadge: Record<string, { label: string; cls: string }> = {
    completed: { label: "validé", cls: "text-emerald-600 bg-emerald-50" },
    missed: { label: "manqué", cls: "text-red-600 bg-red-50" },
    not_applicable: { label: "N/A", cls: "text-neutral-400 bg-neutral-50" },
  };

  const q = query.trim().toLowerCase();
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 flex flex-col gap-3 max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-h2 font-medium text-black">
          Point de contrôle déclencheur — « {stepLabel} »
        </div>
        <div className="text-h4 text-neutral-500">
          Choisissez le ou les points de contrôle dont la validation déclenche
          l'envoi de cette facture. Validez‑les depuis l'onglet Étapes pour
          émettre la facture automatiquement.
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un point de contrôle…"
          className="border border-neutral-300 rounded-lg px-3 py-2 text-h3"
          disabled={submitting}
        />
        <div className="flex-1 overflow-auto -mx-1 px-1 flex flex-col gap-3">
          {themes.map((theme, ti) => {
            const themeLabel = theme.label?.trim() || `Thème ${ti + 1}`;
            const matchingSteps = (theme.steps || [])
              .map((step, si) => {
                const cps = (step.controlpoints || []).filter((cp) => {
                  if (!q) return true;
                  return (
                    (cp.label || "").toLowerCase().includes(q) ||
                    (cp.id || "").toLowerCase().includes(q)
                  );
                });
                return { step, si, cps };
              })
              .filter((s) => s.cps.length > 0);
            if (matchingSteps.length === 0) return null;
            return (
              <div key={theme.id || ti} className="flex flex-col gap-1">
                <div className="text-h4 font-medium text-neutral-700">{themeLabel}</div>
                {matchingSteps.map(({ step, si, cps }) => (
                  <div key={step.id || si} className="flex flex-col">
                    <div className="text-h5 text-neutral-400 pl-1 pt-1">
                      {step.label?.trim() || `Étape ${si + 1}`}
                    </div>
                    {cps.map((cp, ci) => {
                      const badge = stateBadge[cp.state];
                      return (
                        <label
                          key={cp.id || ci}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-neutral-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(cp.id)}
                            onChange={() => toggle(cp.id)}
                            disabled={submitting}
                          />
                          <span className="text-h4 text-neutral-700 flex-1">
                            {cp.label?.trim() || "(sans intitulé)"}
                          </span>
                          {badge && (
                            <span className={`text-h5 px-1.5 py-0.5 rounded ${badge.cls}`}>
                              {badge.label}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
          {themes.length === 0 && (
            <div className="text-h4 text-neutral-400 py-6 text-center">
              Ce projet n'a aucun point de contrôle.
            </div>
          )}
        </div>
        <div className="flex flex-row items-center justify-between pt-1">
          <span className="text-h5 text-neutral-400">
            {selected.size} sélectionné{selected.size > 1 ? "s" : ""}
          </span>
          <div className="flex flex-row gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg text-h3 text-neutral-600 hover:bg-neutral-100"
              onClick={onClose}
              disabled={submitting}
            >
              Annuler
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-lg text-h3 bg-black text-white disabled:opacity-50"
              disabled={submitting}
              onClick={() => onSubmit(Array.from(selected))}
            >
              {submitting ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin-only modal showing the raw error a billing step raised (creation,
// finalize or send). Read-only — just surfaces `invoice_error` so an admin
// can diagnose without digging through logs.
// ---------------------------------------------------------------------------
function BillingErrorModal({
  stepLabel,
  message,
  onClose,
}: {
  stepLabel: string;
  message: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 flex flex-col gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-row items-center gap-2 text-red-600">
          <Icon1 name="warning" size={20} />
          <div className="text-h2 font-medium">Erreur de facturation — « {stepLabel} »</div>
        </div>
        <div className="text-h4 text-neutral-500">
          Détail de l'erreur renvoyée lors de la génération ou de l'envoi de la
          facture.
        </div>
        <pre className="text-h4 text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 whitespace-pre-wrap break-words max-h-80 overflow-auto">
          {message}
        </pre>
        <div className="flex flex-row justify-end gap-2 pt-1">
          <button
            type="button"
            className="px-4 py-2 rounded-lg text-h3 text-neutral-600 hover:bg-neutral-100"
            onClick={copy}
          >
            {copied ? "Copié" : "Copier"}
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-lg text-h3 bg-black text-white"
            onClick={onClose}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
